import {LoadingOutlined, SearchOutlined} from '@ant-design/icons';
import {TermFilter} from '@dofleini/query-builder';
import {Checkbox, Form, Input, Spin} from 'antd';
import classNames from 'classnames';
import find from 'lodash/find';
import compose from 'lodash/fp/compose';
import reduceFP from 'lodash/fp/reduce';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useQuery} from 'react-query';

import createSearchService from '@/utils/createSearchService';

import FilterContent from '../../FilterContent';

const saveTemp = (key) => (value) => {
  localStorage.setItem(key, JSON.stringify(value));
};

const getTemp = (key) => JSON.parse(localStorage.getItem(key));

export const RemoteDataComponent = ({filter, t, applyFilter, currentValue}) => {
  const [searchText, setSearchText] = useState('');

  const [valueKey, labelKey] = useMemo(() => [
    filter?.valueKey || '_id',
    filter?.labelKey || 'name',
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const searchService = useMemo(() => createSearchService(filter.request), [filter.request]);

  const {isLoading, data, refetch} = useQuery([`filter-${filter?.name}`, searchText, filter.filters], async () => {
    const {data} = await searchService({
      size: 50,
      search: searchText,
      filters: filter.filters || {},
      sort: filter.sort || {'createdAt': -1}
    });
    if (data?.length && filter?.supportEmpty)
      data.splice(0, 0, {[labelKey]: t(filter?.emptyLabel || 'none'), [valueKey]: -1});
    return (data);
  });

  useEffect(() => {
    if (currentValue)
      refetch();
  }, [currentValue, refetch]);

  const options = useMemo(() => map(data, item => {
    const value = item[valueKey];
    const label = typeof labelKey === 'function' ? labelKey(item) : item[labelKey];
    return {
      label,
      value,
    };
  }), [data, labelKey, valueKey]);

  const onFinish = useCallback(({value: values}) => {
    compose(
      saveTemp(`filter-${filter?.name}`),
      reduceFP((acc, value) => ({
        ...acc,
        [value]: find(options, {value})?.label
      }), {}),
    )(values);

    let filterOptions = map(values, value => {
      if (!Array.isArray(filter.field)) {
        return new TermFilter({
          field: filter.field,
          value: value === -1 ? null : value,
          objectId: filter.objectId || false,
        }).toQuery();
      } else {
        return {
          'type': 'OR',
          'filters': filter.field?.map((e) => new TermFilter({
            field: e,
            value: value === -1 ? null : value
          }).toQuery())
        };
      }
    });

    applyFilter(
      {
        'type': 'OR',
        'filters': filterOptions
      }
    );
  }, [applyFilter, filter, options]);

  useEffect(() => () => localStorage.removeItem(`filter-${filter?.name}`), [filter.name]);

  return (<>
    <div className="p-2">
      <Input suffix={<SearchOutlined/>} value={searchText} onChange={({target}) => setSearchText(target?.value)}/>
    </div>
    <Form onFinish={onFinish}>
      <FilterContent t={t}>
        {
          isLoading
            ? (
              <div className="flex items-center justify-center py-8">
                <Spin indicator={<LoadingOutlined style={{fontSize: 24}} spin/>}/>
              </div>
            )
            : (
              <Form.Item name="value" className="m-0" initialValue={currentValue || []}>
                <Checkbox.Group className="w-full">
                  <div className="w-full flex flex-col max-w-300px max-h-300px overflow-y-auto">
                    {
                      options.map(({value, label}) => (
                        <Checkbox
                          className={'mx-0 my-px p-2'}
                          key={value}
                          title={label}
                          value={value}>
                          <span className={classNames({'text-primary': value === -1})} title={label}>{label}</span>
                        </Checkbox>
                      ))
                    }
                  </div>
                </Checkbox.Group>
              </Form.Item>
            )
        }
      </FilterContent>
    </Form>
  </>);
};

RemoteDataComponent.propTypes = {
  filter: PropTypes.object,
  currentValue: PropTypes.array,
  t: PropTypes.func,
  applyFilter: PropTypes.func,
};

export const ValueRemoteData = ({filterValue, filter}) => {
  const mapOptions = getTemp(`filter-${filter?.name}`);

  const isArrayField = Array.isArray(filter.field);

  const options = useMemo(() => {
    if (isArrayField && filterValue?.filters) {
      return uniq(Object.values(mapOptions || {})).join(', ');
    } else
      return uniq(map(
        filterValue?.filters, ({value}) => {
          return (value == null) ? mapOptions?.[-1] : mapOptions?.[value];
        }
      )).join(', ');
  }, [filterValue?.filters, isArrayField, mapOptions]);
  return <p className="m-0 break-world leading-snug whitespace-pre-wrap">{options}</p>;
};

ValueRemoteData.propTypes = {
  filter: PropTypes.object,
  t: PropTypes.func,
  filterValue: PropTypes.any,
};
